import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormattingToolbar } from "@/components/common/FormattingToolbar";
import { TextEditor } from "@/components/common/TextEditor";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { CreateChatFormValues } from "@/features/chat/schema";
import { useEditorLinkHover } from "@/hooks/useEditorLinkHover";
import { useEditorSelection } from "@/hooks/useEditorSelection";
import { useFormatting } from "@/hooks/useFormatting";
import { convertTextToMarkdown } from "@/lib/utils/markdownUtils";

interface ChatMessageEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  maxLength: number;
  disabled: boolean;
}

const ChatMessageEditor: React.FC<ChatMessageEditorProps> = ({
  value,
  onChange,
  placeholder,
  maxLength,
  disabled,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [lengthError, setLengthError] = useState<string | null>(null);
  const { hoveredLink, handlePointerOver, handlePointerOut } =
    useEditorLinkHover({ editorRef, containerRef });
  const { restoreSelection, saveCurrentSelection } = useEditorSelection({
    editorRef,
  });
  const formattingHook = useFormatting({ editorRef });

  const syncMarkdown = useCallback(() => {
    if (!editorRef.current) return;

    const markdown = convertTextToMarkdown(editorRef.current, true);
    if (markdown.length > maxLength) {
      setLengthError(t("common.maxLengthExceeded", { maxLength }));
    } else {
      setLengthError(null);
    }
    onChange(markdown);
  }, [maxLength, onChange, t]);

  const handleFormatAction = (action: () => void) => {
    action();
    syncMarkdown();
  };

  useEffect(() => {
    if (!editorRef.current) return;
    if (value.trim().length === 0 && editorRef.current.textContent?.trim()) {
      editorRef.current.innerHTML = "";
      setLengthError(null);
      formattingHook.resetFormatState();
    }
  }, [value, formattingHook]);

  return (
    <div
      ref={containerRef}
      className="base-input-editor relative space-y-2 rounded-md border border-input bg-background px-3 py-2"
      onBlur={(event) => {
        if (isLinkPopoverOpen) {
          return;
        }
        if (
          event.relatedTarget &&
          event.currentTarget.contains(event.relatedTarget as Node)
        ) {
          return;
        }
        setIsFocused(false);
      }}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <TextEditor
        ref={editorRef}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setIsFocused(true)}
        onInput={() => {
          formattingHook.updateFormatState();
          syncMarkdown();
        }}
        onKeyDown={() => {
          formattingHook.updateFormatState();
        }}
        className="min-h-32 text-base leading-6 md:text-sm"
      />
      {hoveredLink && (
        <div
          className="absolute z-50 rounded bg-slate-800 px-2 py-1 text-xs text-white shadow"
          style={{
            top: hoveredLink.top,
            left: hoveredLink.left,
            maxWidth: "300px",
            wordWrap: "break-word",
          }}
        >
          {hoveredLink.url}
        </div>
      )}
      {lengthError && (
        <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-500">
          {lengthError}
        </div>
      )}
      {isFocused && (
        <div
          className="flex flex-wrap gap-2 border-t border-custom-border pt-2"
          onMouseDown={(e) => e.preventDefault()}
        >
          <FormattingToolbar
            isBold={formattingHook.isBold}
            isItalic={formattingHook.isItalic}
            isLink={formattingHook.isLink}
            isCode={formattingHook.isCode}
            isBlockquote={formattingHook.isBlockquote}
            isHeading1={formattingHook.isHeading1}
            isHeading2={formattingHook.isHeading2}
            onToggleBold={() => handleFormatAction(formattingHook.toggleBold)}
            onToggleItalic={() =>
              handleFormatAction(formattingHook.toggleItalic)
            }
            onApplyLink={(url) => {
              restoreSelection();
              handleFormatAction(() => formattingHook.applyLink(url));
            }}
            onRemoveLink={() => {
              restoreSelection();
              handleFormatAction(formattingHook.removeLink);
            }}
            onBeforeLinkEdit={saveCurrentSelection}
            onToggleCode={() => handleFormatAction(formattingHook.toggleCode)}
            onToggleBlockquote={() =>
              handleFormatAction(formattingHook.toggleBlockquote)
            }
            onToggleHeading1={() =>
              handleFormatAction(formattingHook.toggleHeading1)
            }
            onToggleHeading2={() =>
              handleFormatAction(formattingHook.toggleHeading2)
            }
            showAttachButton={false}
            onLinkPopoverOpenChange={setIsLinkPopoverOpen}
          />
        </div>
      )}
    </div>
  );
};

interface ChatFormProps {
  form: UseFormReturn<CreateChatFormValues>;
  titleMaxLength?: number;
  messageMaxLength?: number;
  disabled?: boolean;
  titleMode?: "optional" | "required" | "hidden";
  messageLabelKey?: string;
}

export const ChatForm: React.FC<ChatFormProps> = ({
  form,
  titleMaxLength = 30,
  messageMaxLength = 500,
  disabled = false,
  titleMode = "optional",
  messageLabelKey = "chat.sendMessage",
}) => {
  const { t } = useTranslation();
  const [isTitleOpen, setIsTitleOpen] = useState(false);

  const isTitleRequired = titleMode === "required";
  const isTitleOptional = titleMode === "optional";
  const isTitleVisible = isTitleRequired || isTitleOpen;

  const titleLength = form.watch("title")?.length ?? 0;
  return (
    <div className="space-y-1">
      <div className="text-sm font-semibold text-text-subtitle">
        {t(messageLabelKey)}
      </div>
      {isTitleOptional ? (
        <Button
          type="button"
          onClick={() => setIsTitleOpen((prev) => !prev)}
          variant="outline"
          disabled={disabled}
        >
          {t("chat.addTitleOptional")}
        </Button>
      ) : null}

      {isTitleVisible && titleMode !== "hidden" && (
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <span className="text-xs text-muted-foreground">
                  {titleLength}/{titleMaxLength}
                </span>
              </div>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("chat.titlePlaceholder")}
                  maxLength={titleMaxLength}
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <ChatMessageEditor
                value={field.value ?? ""}
                onChange={field.onChange}
                placeholder={t("chat.messagePlaceholder")}
                maxLength={messageMaxLength}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
